// Recent users + recent orders tables (reference design).
import React from "react";
import { Link } from "react-router-dom";
import { Card, Pill, statusTone } from "./dashboardShared";

// Avatar + Type-pill tint by role (themeable token pairs)
const ROLE_TINT = {
  admin: { bg: "var(--color-danger-bg)", color: "var(--color-danger-text)" },
  sub_admin: { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
  subadmin: { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
  supplier: { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
  buyer: { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
  default: { bg: "var(--color-surface-2)", color: "var(--color-text-secondary)" },
};

const tint = (role) => ROLE_TINT[role] || ROLE_TINT.default;

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const Avatar = ({ name, role }) => {
  const t = tint(role);
  return (
    <div className="idcell__avatar" style={{ background: t.bg, color: t.color }}>
      {initials(name)}
    </div>
  );
};

const TypePill = ({ role }) => {
  const t = tint(role);
  return (
    <span className="pill pill--cap" style={{ background: t.bg, color: t.color }}>
      {(role || "").replaceAll("_", " ")}
    </span>
  );
};

// Order-status labels matching the reference wording (lowercase)
const ORDER_LABEL = {
  pending: "pending",
  sales_executive_assigned: "sales exec assigned",
  design_received: "design received",
  graphic_designer_assigned: "graphic designer assigned",
  quotation_received: "quotation received",
  order_tracking: "order tracking",
  order_completed: "completed",
};
const orderLabel = (s) => ORDER_LABEL[s] || (s || "").replaceAll("_", " ");

const StatusDot = ({ status }) => {
  const ok = status === "active";
  return (
    <span
      className="pill"
      style={{
        gap: 5,
        background: ok ? "var(--color-success-bg)" : "var(--color-warning-bg)",
        color: ok ? "var(--color-success-text)" : "var(--color-warning-text)",
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "currentColor",
        }}
      />
      {status}
    </span>
  );
};

export default function RecentActivity({ recentUsers, recentOrders }) {
  return (
    <section className="section">
      <div className="grid grid--two">
        {/* Recent Users */}
        <Card
          title="Recent Users"
          right={
            <Link className="card__link" to="/admin/usermanagement/list">
              See all
            </Link>
          }
        >
          <div className="table-wrapper">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Type</th>
                  <th style={{ textAlign: "right" }}>Status / Date</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.map((u) => (
                  <tr key={u.user_id}>
                    <td>
                      <div className="idcell">
                        <Avatar name={u.name} role={u.user_type} />
                        <div style={{ minWidth: 0 }}>
                          <div className="idcell__name">{u.name}</div>
                          <div className="idcell__sub">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <TypePill role={u.user_type} />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <StatusDot status={u.status} />
                      <div className="ts" style={{ marginTop: 3 }}>
                        {new Date(u.created_at).toLocaleString("en-IN")}
                      </div>
                    </td>
                  </tr>
                ))}
                {recentUsers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="empty">
                      No users
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Recent Orders */}
        <Card
          title="Recent Orders"
          right={
            <Link className="card__link" to="/admin/assign-order">
              See all
            </Link>
          }
        >
          <div className="table-wrapper">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Buyer</th>
                  <th style={{ textAlign: "right" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o) => (
                  <tr key={o.order_id}>
                    <td className="bold">#{o.order_id}</td>
                    <td>
                      <div className="idcell__name">{o?.buyer?.name || "-"}</div>
                      <div className="idcell__sub" style={{ maxWidth: 180 }}>
                        {o?.product?.title || "-"}
                      </div>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Pill tone={statusTone(o.order_status)}>
                        {orderLabel(o.order_status)}
                      </Pill>
                      <div className="ts" style={{ marginTop: 3 }}>
                        {new Date(o.created_at).toLocaleString("en-IN")}
                      </div>
                    </td>
                  </tr>
                ))}
                {recentOrders.length === 0 && (
                  <tr>
                    <td colSpan={3} className="empty">
                      No orders
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </section>
  );
}
